import React, { ReactNode, useState, useRef, useEffect } from 'react'
import { ColumnSettings } from '@/shared/hooks/useTableCustomization'
import { useTranslation } from '@/shared/contexts/TranslationContext'

export interface DataTableColumn<T> {
  key: string
  label: string
  render?: (item: T) => ReactNode
  width?: string
  align?: 'left' | 'center' | 'right'
  resizable?: boolean
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  onRowClick?: (item: T) => void
  
  // Bulk selection props (optional)
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelection?: (id: string) => void
  onToggleAll?: () => void
  isAllSelected?: boolean
  isSomeSelected?: boolean
  // Optional predicate to determine whether a given row is selectable
  isItemSelectable?: (item: T) => boolean
  
  // Customization props (optional)
  customization?: {
    columnSettings?: Record<string, ColumnSettings>
    densityClasses?: {
      headerPadding: string
      cellPadding: string
      textSize: string
    }
    onColumnResize?: (columnKey: string, width: string) => void
  }
  // Optional per-row stamp watermark (e.g. 'PAGO')
  getRowStamp?: (item: T) => string | null | undefined
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  selectable = false,
  selectedIds = new Set(),
  onToggleSelection,
  onToggleAll,
  isAllSelected = false,
  isSomeSelected = false,
  isItemSelectable = () => true,
  customization,
  getRowStamp,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  const [resizingColumn, setResizingColumn] = useState<string | null>(null)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const tableRef = useRef<HTMLDivElement>(null)
  const startXRef = useRef<number>(0)
  const startWidthRef = useRef<number>(0)

  const getAlignClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center': return 'text-center'
      case 'right': return 'text-right'
      default: return 'text-left'
    }
  }

  // Classes de densidade (usa customization ou valores padrão)
  const densityClasses = customization?.densityClasses || {
    headerPadding: 'px-6 py-3',
    cellPadding: 'px-6 py-4',
    textSize: 'text-sm'
  }

  // Filtrar colunas visíveis e ordenar de acordo com as configurações
  const visibleColumns = React.useMemo(() => {
    if (!customization?.columnSettings) return columns
    const cols = columns
      .filter(col => customization.columnSettings?.[col.key]?.visible !== false)
      .sort((a, b) => {
        const orderA = customization.columnSettings?.[a.key]?.order ?? 999
        const orderB = customization.columnSettings?.[b.key]?.order ?? 999
        return orderA - orderB
      })
      .map(col => ({
        ...col,
        width: customization.columnSettings?.[col.key]?.width || col.width
      }))

    // Ensure actions column stays as the last column regardless of saved ordering
    const actionsIndex = cols.findIndex(c => c.key === 'actions')
    if (actionsIndex >= 0) {
      const [actionsCol] = cols.splice(actionsIndex, 1)
      cols.push(actionsCol)
    }

    return cols
  }, [columns, customization?.columnSettings])

  const hasSelectableItems = selectable && data.some(item => isItemSelectable(item))

  // Handler para iniciar redimensionamento
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    
    const th = (e.target as HTMLElement).closest('th')
    if (!th) return

    setResizingColumn(columnKey)
    startXRef.current = e.clientX
    startWidthRef.current = th.offsetWidth
  }

  // Handler para redimensionar
  useEffect(() => {
    if (!resizingColumn) return

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startXRef.current
      const newWidth = Math.max(80, startWidthRef.current + diff)
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: newWidth
      }))
    }

    const handleMouseUp = () => {
      if (resizingColumn && columnWidths[resizingColumn]) {
        customization?.onColumnResize?.(resizingColumn, `${columnWidths[resizingColumn]}px`)
      }
      setResizingColumn(null)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [resizingColumn, columnWidths, customization])

  return (
    <div 
      ref={tableRef}
      className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden ${
        resizingColumn ? 'cursor-col-resize select-none' : ''
      }`}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
            <tr>
              {/* Selection checkbox column */}
              {selectable && (
                <th className={`${densityClasses.headerPadding} w-12`}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate = isSomeSelected
                      }
                    }}
                    onChange={onToggleAll}
                    disabled={!hasSelectableItems}
                    className="w-4 h-4 text-emerald-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={isAllSelected ? t('list.unselectAll') : t('list.selectAll')}
                  />
                </th>
              )}
              {visibleColumns.map((column) => {
                const currentWidth = columnWidths[column.key]
                const width = currentWidth ? `${currentWidth}px` : column.width
                const isResizing = resizingColumn === column.key

                return (
                  <th
                    key={column.key}
                    className={`${densityClasses.headerPadding} ${densityClasses.textSize} font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider ${getAlignClass(column.align)} relative group ${
                      isResizing ? 'bg-emerald-100 dark:bg-emerald-900/30' : ''
                    }`}
                    style={width ? { width, minWidth: width } : undefined}
                  >
                    {column.label}
                    
                    {/* Resize handle - área interativa maior para facilitar o uso */}
                    {column.resizable !== false && (
                      <div
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize group/resize"
                        onMouseDown={(e) => handleResizeStart(e, column.key)}
                        title="Arrastar para redimensionar"
                      >
                        {/* Indicador visual */}
                        <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-slate-300 dark:bg-slate-600 opacity-0 group-hover:opacity-50 group-hover/resize:!opacity-100 group-hover/resize:bg-emerald-500 dark:group-hover/resize:bg-emerald-400 transition-all" />
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {data.map((item) => {
              const itemId = keyExtractor(item)
              const isSelected = selectedIds.has(itemId)
              const rowStamp = getRowStamp?.(item) || null
              
              return (
                <tr
                  key={itemId}
                  style={rowStamp ? { position: 'relative' } : undefined}
                  className={`transition-colors ${
                    isSelected
                      ? 'bg-emerald-50 dark:bg-emerald-900/20'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {/* Selection checkbox cell */}
                  {selectable && (
                    <td 
                      className={densityClasses.cellPadding}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(() => {
                        const itemSelectable = isItemSelectable(item)
                        return (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => { if (itemSelectable) onToggleSelection?.(itemId) }}
                            disabled={!itemSelectable}
                            className="w-4 h-4 text-emerald-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={t('list.selectItem', { id: itemId })}
                          />
                        )
                      })()}
                    </td>
                  )}
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      className={`${densityClasses.cellPadding} ${densityClasses.textSize} text-slate-700 dark:text-slate-200 ${getAlignClass(column.align)}`}
                    >
                      {column.render ? column.render(item) : (item as any)[column.key]}
                    </td>
                  ))}
                  {/* Horizontal stamp overlay for the row */}
                  {rowStamp && (
                    <td
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        padding: 0,
                        overflow: 'hidden',
                        border: 'none',
                      }}
                    >
                      <div className="flex items-center justify-center h-full">
                        <div
                          className="text-emerald-600 dark:text-emerald-400 border-[3px] border-current rounded"
                          style={{
                            opacity: 0.35,
                            padding: '6px 28px',
                            lineHeight: 1,
                          }}
                        >
                          <span
                            className="block font-black uppercase select-none"
                            style={{ fontSize: 'clamp(1.4rem, 3vw, 1.75rem)', letterSpacing: '0.6em' }}
                          >
                            {rowStamp}
                          </span>
                        </div>
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
